import React from 'react';
import { Typography } from '@mui/material';

import { useServiceInterruptions } from 'store/apis';
import { Column, Row } from 'components/cc-tables/cc-table.context';
import { PopupActionsCell } from 'components/cc-tables/cc-table-cells';

type UseServiceInterruptionsTableProps = {
  onDelete: (id: number) => void;
};

export const useServiceInterruptionsTable = ({ onDelete }: UseServiceInterruptionsTableProps) => {
  const { data: serviceInterruptions = [], ...rest } = useServiceInterruptions();

  const columns: Column[] = [
    {
      id: 'dateCol',
      label: 'Date',
      render: ({ dateCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {dateCol}
        </Typography>
      ),
      sorter: ({ createdAtTimestamp: val1 }: Row, { createdAtTimestamp: val2 }: Row) => {
        return val2 - val1;
      },
      filter: {
        type: 'date',
        func:
          (value: any) =>
          ({ createdAtTimestamp }) => {
            if (!value) {
              return true;
            }

            const { start, end } = value;

            const startTimestamp = start.valueOf();
            const endTimestamp = end.valueOf();

            return createdAtTimestamp >= startTimestamp && createdAtTimestamp < endTimestamp;
          },
      },
    },
    {
      id: 'buildingCol',
      label: 'Building',
      render: ({ buildingCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {buildingCol}
        </Typography>
      ),
    },
    {
      id: 'zoneCol',
      label: 'Zone #',
      filter: false,
      render: ({ zoneCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {zoneCol}
        </Typography>
      ),
    },
    {
      id: 'scheduleCol',
      label: 'Schedule',
      render: ({ scheduleCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {scheduleCol}
        </Typography>
      ),
      sorter: ({ schedule: { scheduleTimestamp: val1 } }: Row, { schedule: { scheduleTimestamp: val2 } }: Row) => {
        return val2 - val1;
      },
      filter: false,
    },
    {
      id: 'impactBuildingCol',
      label: 'Impacts Entire Building?',
      render: ({ impactBuildingCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {impactBuildingCol}
        </Typography>
      ),
      filter: {
        type: 'checkbox',
        componentProps: {
          options: [
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ],
        },
        func:
          (value) =>
          ({ impactBuildingCol }) => {
            return value.length === 0 || value.includes(impactBuildingCol);
          },
      },
    },
    {
      id: 'commentCol',
      label: 'Comment',
      render: ({ commentCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {commentCol}
        </Typography>
      ),
    },
    {
      id: 'supervisorCol',
      label: 'Supervisor',
      render: ({ supervisorCol, status }) => (
        <Typography variant="body2" color={status === 'active' ? 'textPrimary' : 'textSecondary'}>
          {supervisorCol}
        </Typography>
      ),
    },
    {
      id: 'statusCol',
      label: 'Status',
      render: ({ status, statusString }) => (
        <Typography
          variant="body2"
          sx={{
            color: (theme) => (status === 'active' ? theme.palette.success.light : theme.palette.warning.main),
            fontWeight: '600',
          }}
        >
          {statusString}
        </Typography>
      ),
      filter: {
        type: 'checkbox',
        componentProps: {
          options: [
            { value: 'active', label: 'Active' },
            { value: 'expired', label: 'Expired' },
            { value: 'canceled', label: 'Canceled' },
          ],
        },
        func:
          (value) =>
          ({ status }) => {
            return value.length === 0 || value.includes(status);
          },
      },
    },
    {
      id: 'actionsCol',
      label: 'Actions',
      render: ({ actionsCol, status }) => (status === 'active' ? <PopupActionsCell {...actionsCol} /> : null),
      sorter: false,
      filter: false,
      mobileSx: { position: 'absolute', top: 8, right: 8 },
      align: 'right',
    },
  ];

  const rows: Row[] = serviceInterruptions.map((item) => {
    const {
      id,
      createdAtString: dateCol,
      schedule,
      building_id,
      building: { fullLocation: buildingCol },
      zone: { code },
      comment: commentCol,
      creator: { name: supervisorCol },
      statusString: statusCol,
    } = item;

    const zoneCol = `#${code}`;
    const impactBuildingCol = building_id ? 'Yes' : 'No';

    const actionsCol = {
      items: [
        {
          id: 1,
          label: 'Cancel',
          onClick: () => onDelete(id),
        },
      ],
    };

    let scheduleCol;
    if (schedule) {
      scheduleCol = schedule.scheduleTimeString;
    }

    return {
      ...item,
      id,
      dateCol,
      scheduleCol,
      buildingCol,
      zoneCol,
      impactBuildingCol,
      commentCol,
      supervisorCol,
      statusCol,
      actionsCol,
    };
  });

  return { data: { columns, rows }, ...rest };
};
